import { useEffect, useMemo, useState } from 'react'
import { useUser } from '../../hooks/useUser'
import { useNotificationContext } from '../../contexts/NotificationContext'
import {
  Realization, RealizationStats, Stage, STAGES, OFFICE_STAGES,
  stageMeta, daysInStage, authHeaders,
} from './realizationConstants'
import { MediaBadges, ConditionsExpiryBadge } from './RealizationBadges'
import RealizationDetailModal from './RealizationDetailModal'
import RealizationForm from './RealizationForm'

const PIPELINE: Stage[] = ['application', 'analysis', 'conditions', 'design', 'agreement', 'fieldwork', 'acceptance', 'contract', 'completed']
const CLOSED: Stage[] = ['rejected', 'cancelled']

export default function RealizationsBoard({ token }: { token: string }) {
  const { user, canManageRealizations } = useUser(token)
  const canManage = canManageRealizations()
  const isMaster = user?.role === 'MASTER_ADMIN'
  const { showSuccess, showError } = useNotificationContext()
  const [items, setItems] = useState<Realization[]>([])
  const [stats, setStats] = useState<RealizationStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'board' | 'list'>('board')
  const [search, setSearch] = useState('')
  const [onlyMine, setOnlyMine] = useState(false)
  const [showClosed, setShowClosed] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Realization | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const [lRes, sRes] = await Promise.all([
        fetch('/api/realizations/', { headers: authHeaders(token) }),
        fetch('/api/realizations/stats', { headers: authHeaders(token) }),
      ])
      setItems(lRes.ok ? await lRes.json() : [])
      setStats(sRes.ok ? await sRes.json() : null)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((r) => {
      if (onlyMine && !(r.status === 'active' && OFFICE_STAGES.includes(r.stage))) return false
      if (q) {
        const hay = `${r.applicant_name} ${r.location} ${r.plot_number}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [items, search, onlyMine])

  const columns = useMemo(() => {
    const cols = showClosed ? [...PIPELINE, ...CLOSED] : PIPELINE
    const map: Record<string, Realization[]> = {}
    cols.forEach((c) => { map[c] = [] })
    filtered.forEach((r) => { if (map[r.stage]) map[r.stage].push(r) })
    return { cols, map }
  }, [filtered, showClosed])

  const openDetail = (id: number) => setDetailId(id)
  const openCreate = () => { setEditing(null); setFormOpen(true) }
  const openEdit = (r: Realization) => { setDetailId(null); setEditing(r); setFormOpen(true) }

  const removeRealization = async (r: Realization) => {
    if (!window.confirm(`Trwale usunąć realizację „${r.applicant_name}” (${r.location})? Tej operacji nie można cofnąć.`)) return
    try {
      const res = await fetch(`/api/realizations/${r.id}`, { method: 'DELETE', headers: authHeaders(token) })
      if (res.ok) { showSuccess('Usunięto realizację'); load() }
      else {
        const err = await res.json().catch(() => ({}))
        showError('Błąd', err.detail || 'Nie udało się usunąć')
      }
    } catch { showError('Błąd', 'Błąd połączenia') }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Realizacje przyłączeń</h1>
          <p className="text-sm text-gray-500">Wnioski o warunki i przyłączenie do sieci wod-kan</p>
        </div>
        {canManage && (
          <button onClick={openCreate} className="min-h-[44px] px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
            + Nowy wniosek
          </button>
        )}
      </div>

      {/* Kafle */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Tile label="Aktywne" value={stats.active} />
          <Tile label="Do mnie" value={stats.todo_office} accent="#2563eb" onClick={() => setOnlyMine(true)} />
          <Tile label="Warunki: kończą się" value={stats.conditions_expiring} accent="#d97706" />
          <Tile label="Warunki: po terminie" value={stats.conditions_expired} accent="#dc2626" />
          <Tile label="Zakończone" value={stats.by_stage?.completed || 0} accent="#16a34a" />
        </div>
      )}

      {/* Pasek narzędzi */}
      <div className="flex flex-wrap items-center gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj: wnioskodawca / miejscowość / działka"
          className="flex-1 min-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <Toggle active={onlyMine} onClick={() => setOnlyMine(!onlyMine)}>Do mnie</Toggle>
        <Toggle active={showClosed} onClick={() => setShowClosed(!showClosed)}>Pokaż zamknięte</Toggle>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button onClick={() => setView('board')} className={`px-3 py-2 text-sm ${view === 'board' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Tablica</button>
          <button onClick={() => setView('list')} className={`px-3 py-2 text-sm ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Lista</button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />Ładowanie…
        </div>
      ) : view === 'board' ? (
        <div className="flex gap-3 overflow-x-auto pb-3">
          {columns.cols.map((stage) => {
            const m = STAGES[stage]
            const list = columns.map[stage]
            return (
              <div key={stage} className="w-72 shrink-0">
                <div className={`rounded-t-lg px-3 py-2 ${m.bg} flex items-center justify-between`}>
                  <span className="text-sm font-semibold text-gray-800">{m.short}</span>
                  <span className="text-xs text-gray-600 bg-white/70 rounded-full px-2 py-0.5">{list.length}</span>
                </div>
                <div className="bg-gray-50 rounded-b-lg p-2 space-y-2 min-h-[120px]">
                  {list.map((r) => <Card key={r.id} r={r} onClick={() => openDetail(r.id)} onDelete={isMaster ? () => removeRealization(r) : undefined} />)}
                  {list.length === 0 && <p className="text-xs text-gray-400 text-center py-4">—</p>}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <ListView items={filtered} onOpen={openDetail} onDelete={isMaster ? removeRealization : undefined} />
      )}

      {detailId !== null && (
        <RealizationDetailModal token={token} realizationId={detailId} canManage={canManage}
          onClose={() => setDetailId(null)} onChanged={load} onEdit={openEdit} />
      )}
      {formOpen && (
        <RealizationForm token={token} editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load() }} />
      )}
    </div>
  )
}

function Tile({ label, value, accent, onClick }: { label: string; value: number; accent?: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`text-left rounded-xl border border-gray-200 bg-white p-3 ${onClick ? 'hover:border-gray-300 cursor-pointer' : 'cursor-default'}`}>
      <div className="text-2xl font-bold" style={{ color: accent || '#111827' }}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </button>
  )
}

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={`min-h-[40px] px-3 rounded-lg text-sm font-medium ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
      {children}
    </button>
  )
}

function Card({ r, onClick, onDelete }: { r: Realization; onClick: () => void; onDelete?: () => void }) {
  const m = stageMeta(r.stage)
  const days = daysInStage(r)
  const stale = r.status === 'active' && days >= 14
  return (
    <div className="relative group">
      <button onClick={onClick} className="w-full text-left rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm p-2.5 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900 truncate">{r.applicant_name}</span>
          {m.owner === 'office' && r.status === 'active' && (
            <span title="Piłka po stronie urzędu" className="shrink-0 w-2.5 h-2.5 rounded-full bg-blue-500 mt-1" />
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">{r.location} • dz. {r.plot_number}</p>
        <div className="flex flex-wrap items-center gap-1">
          <MediaBadges r={r} size="xs" />
          <ConditionsExpiryBadge r={r} />
          {r.contract_number && <span className="text-[10px] text-teal-700">📄 {r.contract_number}</span>}
        </div>
        {r.status === 'active' && (
          <p className={`text-[11px] ${stale ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>stoi {days} dni{stale ? ' ⚠' : ''}</p>
        )}
      </button>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete() }}
          title="Usuń (master admin)"
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded bg-white/90 border border-gray-200 text-red-600 text-xs hover:bg-red-50">🗑</button>
      )}
    </div>
  )
}

function ListView({ items, onOpen, onDelete }: { items: Realization[]; onOpen: (id: number) => void; onDelete?: (r: Realization) => void }) {
  if (items.length === 0) return <p className="py-12 text-center text-gray-400 text-sm">Brak realizacji.</p>
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Wnioskodawca', 'Lokalizacja', 'Media', 'Etap', 'Umowa', 'Stoi'].map((h) => (
              <th key={h} className="px-4 py-2 text-left font-medium text-gray-500">{h}</th>
            ))}
            {onDelete && <th className="px-4 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((r) => {
            const m = stageMeta(r.stage)
            return (
              <tr key={r.id} onClick={() => onOpen(r.id)} className="hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-2 font-medium text-gray-900">{r.applicant_name}</td>
                <td className="px-4 py-2 text-gray-600">{r.location} • dz. {r.plot_number}</td>
                <td className="px-4 py-2"><MediaBadges r={r} size="xs" /></td>
                <td className="px-4 py-2"><span className="rounded-full text-xs px-2 py-0.5 font-medium" style={{ backgroundColor: m.color + '22', color: m.color }}>{m.label}</span></td>
                <td className="px-4 py-2 text-gray-600">{r.contract_number ? `${r.contract_number}${r.has_inventory ? ' • inw.' : ''}` : '—'}</td>
                <td className="px-4 py-2 text-gray-500">{r.status === 'active' ? `${daysInStage(r)} dni` : '—'}</td>
                {onDelete && (
                  <td className="px-4 py-2 text-right">
                    <button onClick={(e) => { e.stopPropagation(); onDelete(r) }}
                      className="text-red-600 hover:text-red-800 text-sm" title="Usuń (master admin)">🗑 Usuń</button>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
